import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { Eye, LogOut, Filter, X, UserPlus, Share2, Trash2, ChevronLeft, ChevronRight, Calendar, Users, Download, Gift, User, Building, Lock, Coffee as CoffeeIcon, Settings, Bell, RefreshCw, Mail, Sparkles, Megaphone, Globe } from 'lucide-react';
import Lenis from '@studio-freight/lenis';
import { motion } from 'framer-motion';
import ConsultationList from '@/components/admin/ConsultationList';
import ActivityLogs from '@/components/admin/ActivityLogs';
import ExportModal from '@/components/admin/ExportModal';
import { hasPermission, canDelete, canExport, isAdmin } from '@/utils/permissions';
import AvailabilityTab from '@/components/admin/AvailabilityTab';
import AlertsTab from '@/components/admin/AlertsTab';
import CRMSyncTab from '@/components/admin/CRMSyncTab';
import PpcAdsTab from '@/components/admin/PpcAdsTab';
import HealthCheckTab from '@/components/admin/HealthCheckTab';
import PenaltyRecoveryTab from '@/components/admin/PenaltyRecoveryTab';
import SubscriptionsTab from '@/components/admin/SubscriptionsTab';
import GYAQueueTab from '@/components/admin/GYAQueueTab';
import IndexingFixTab from '@/components/admin/IndexingFixTab';
import LeadboardShell from '@/components/leadboard/LeadboardShell';
import Overview from '@/components/leadboard/Overview';
import LeadsTab from '@/components/leadboard/tabs/LeadsTab';
import BridgePlaceholder from '@/components/leadboard/BridgePlaceholder';
import BillingTab from '@/components/leadboard/BillingTab';
import { isTabAllowed, defaultTabFor } from '@/components/leadboard/nav';
import './LeadBoard.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Field presets for quick selection
const FIELD_PRESETS = {
  basic_info: {
    label: 'Basic Info',
    fields: ['first_name', 'last_name', 'email', 'phone', 'business_name', 'location']
  },
  sales_team: {
    label: 'Sales Team',
    fields: ['first_name', 'last_name', 'email', 'phone', 'business_name', 'location', 'industry', 'annual_revenue', 'services', 'timeline', 'grade', 'score', 'story']
  },
  account_manager: {
    label: 'Account Manager',
    fields: ['first_name', 'last_name', 'email', 'phone', 'business_name', 'location', 'industry', 'annual_revenue', 'accounting_software', 'services', 'values_fit', 'referral_source']
  },
  full: {
    label: 'Full Details',
    fields: ['first_name', 'last_name', 'email', 'phone', 'business_name', 'location', 'industry', 'annual_revenue', 'accounting_software', 'services', 'timeline', 'values_fit', 'story', 'referral_source', 'referrer_name', 'grade', 'score', 'created_at', 'status']
  }
};

// All available fields for manual selection
const ALL_FIELDS = [
  { value: 'first_name', label: 'First Name' },
  { value: 'last_name', label: 'Last Name' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'business_name', label: 'Business Name' },
  { value: 'location', label: 'Location' },
  { value: 'industry', label: 'Industry' },
  { value: 'annual_revenue', label: 'Annual Revenue' },
  { value: 'accounting_software', label: 'Accounting Software' },
  { value: 'services', label: 'Services' },
  { value: 'timeline', label: 'Timeline' },
  { value: 'values_fit', label: 'Values Fit' },
  { value: 'story', label: 'Story' },
  { value: 'referral_source', label: 'Referral Source' },
  { value: 'referrer_name', label: 'Referrer Name' },
  { value: 'grade', label: 'Grade' },
  { value: 'score', label: 'Score' },
  { value: 'created_at', label: 'Created Date' },
  { value: 'status', label: 'Status' }
];

export default function LeadBoard() {
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sessionToken, setSessionToken] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [teamError, setTeamError] = useState('');
  const [inviteError, setInviteError] = useState('');
  const [editError, setEditError] = useState('');
  const [currentUser, setCurrentUser] = useState(null); // Store user role and permissions
  const navigate = useNavigate(); // For navigation to lead detail page
  
  // Tab state — Phase 1 redesign adds 'overview', 'bridge', 'billing' to the existing 17 keys.
  // Sources of truth: /app/frontend/src/components/leadboard/nav.js
  const [activeTab, setActiveTab] = useState('overview');

  // Data state
  const [leads, setLeads] = useState([]);
  const [holidayLeads, setHolidayLeads] = useState([]);
  const [lttBookings, setLttBookings] = useState([]);
  const [totalLttBookings, setTotalLttBookings] = useState(0);
  const [isLoadingLtt, setIsLoadingLtt] = useState(false);
  const [referrals, setReferrals] = useState([]);
  const [totalReferrals, setTotalReferrals] = useState(0);
  const [isLoadingReferrals, setIsLoadingReferrals] = useState(false);
  const [preRegistrations, setPreRegistrations] = useState([]);
  const [isLoadingPreReg, setIsLoadingPreReg] = useState(false);
  const [coffeeLeads, setCoffeeLeads] = useState([]);
  const [isLoadingCoffee, setIsLoadingCoffee] = useState(false);
  const [lttKgobLeads, setLttKgobLeads] = useState([]);
  const [lttKgobStats, setLttKgobStats] = useState({ new: 0, contacted: 0, converted: 0 });
  const [isLoadingLttKgob, setIsLoadingLttKgob] = useState(false);




  const [contacts, setContacts] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [filteredHolidayLeads, setFilteredHolidayLeads] = useState([]);
  const [isLoadingLeads, setIsLoadingLeads] = useState(false);
  const [leadsError, setLeadsError] = useState('');
  const [isLoadingHolidayLeads, setIsLoadingHolidayLeads] = useState(false);
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);
  const [teamMembersList, setTeamMembersList] = useState([]);

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [gradeFilter, setGradeFilter] = useState('all');
  const [dateRange, setDateRange] = useState('');  // Phase 3: date-range filter (1d/7d/30d/90d) for LeadsTab
  
  // Holiday leads filter state
  const [holidaySearchTerm, setHolidaySearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState('all');
  const [interestFilter, setInterestFilter] = useState('all');
  const [consentFilter, setConsentFilter] = useState('all');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Bulk selection state
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [selectedHolidayLeads, setSelectedHolidayLeads] = useState([]);
  const [isDeletingBulk, setIsDeletingBulk] = useState(false);

  // Export state
  const [showExportModal, setShowExportModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Smooth scroll ref
  const lenisRef = useRef(null);

  // Modal state
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [showShareLeadModal, setShowShareLeadModal] = useState(false);

  const [leadToShare, setLeadToShare] = useState(null);

  // Contact form state
  const [newContactName, setNewContactName] = useState('');
  const [newContactEmail, setNewContactEmail] = useState('');
  const [newContactRole, setNewContactRole] = useState('');
  const [isAddingContact, setIsAddingContact] = useState(false);

  // Team management state
  const [teamMembers, setTeamMembers] = useState([]);
  const [isLoadingTeam, setIsLoadingTeam] = useState(false);
  // Team invitation modal
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [invitePermissions, setInvitePermissions] = useState({
    leads: { view: false, edit: false, delete: false, export: false, add_comments: false },
    consultations: { view: false, edit: false, approve_reject: false, delete: false, add_comments: false },
    analytics: { view_own: false, view_team: false },
  });
  const [isSendingInvite, setIsSendingInvite] = useState(false);

  // Team edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPermissions, setEditPermissions] = useState({
    leads: { view: false, edit: false, delete: false, export: false, add_comments: false },
    consultations: { view: false, edit: false, approve_reject: false, delete: false, add_comments: false },
    analytics: { view_own: false, view_team: false },
  });
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // Share lead state
  const [selectedPreset, setSelectedPreset] = useState('');
  const [selectedFields, setSelectedFields] = useState([]);
  const [shareContactEmail, setShareContactEmail] = useState('');
  const [isSharingLead, setIsSharingLead] = useState(false);

  // Check for existing session on mount
  useEffect(() => {
    const token = localStorage.getItem('leadboard_session_token');
    const userInfo = localStorage.getItem('leadboard_user_info');
    if (token) {
      setSessionToken(token);
      setIsAuthenticated(true);
      if (userInfo) {
        try {
          setCurrentUser(JSON.parse(userInfo));
        } catch (e) {
          console.error('Error parsing user info:', e);
        }
      }
    }
  }, []);

  // Fetch leads when authenticated
  useEffect(() => {
    if (isAuthenticated && sessionToken) {
      fetchLeads();
      fetchContacts();
      fetchTeamMembersList(); // Fetch team members for assignment dropdown
    }
  }, [isAuthenticated, sessionToken]);

  // Fetch team members when Team tab is active
  useEffect(() => {
    if (isAuthenticated && sessionToken && activeTab === 'team' && isAdmin(currentUser)) {
      fetchTeamMembers();
    }
  }, [isAuthenticated, sessionToken, activeTab, currentUser]);

  // Fetch holiday leads when Holiday tab is active
  useEffect(() => {
    if (isAuthenticated && sessionToken && activeTab === 'holiday') {
      fetchHolidayLeads();
    }
  }, [isAuthenticated, sessionToken, activeTab]);

  // Fetch LetsTalkTaxes bookings when LTT tab is active
  useEffect(() => {
    if (isAuthenticated && sessionToken && activeTab === 'letstalktaxes') {
      fetchLttBookings();
    }
  }, [isAuthenticated, sessionToken, activeTab]);

  // Apply filters
  useEffect(() => {
    let filtered = [...leads];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = (filtered || []).filter(lead =>
        (lead.first_name?.toLowerCase().includes(term)) ||
        (lead.last_name?.toLowerCase().includes(term)) ||
        (lead.email?.toLowerCase().includes(term)) ||
        (lead.business_name?.toLowerCase().includes(term))
      );
    }

    // Status filter
    if (statusFilter && statusFilter !== 'all') {
      filtered = (filtered || []).filter(lead => lead.status === statusFilter);
    }

    // Grade filter
    if (gradeFilter && gradeFilter !== 'all') {
      filtered = (filtered || []).filter(lead => lead.grade === gradeFilter);
    }

    // Date range filter (Phase 3): "1d" | "7d" | "30d" | "90d"
    if (dateRange) {
      const days = parseInt(dateRange, 10);
      if (!Number.isNaN(days)) {
        const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
        filtered = (filtered || []).filter((lead) => {
          if (!lead.created_at) return false;
          return new Date(lead.created_at).getTime() >= cutoff;
        });
      }
    }

    setFilteredLeads(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [leads, searchTerm, statusFilter, gradeFilter, dateRange]);

  // Apply filters for holiday leads
  useEffect(() => {
    let filtered = [...holidayLeads];

    // Search filter
    if (holidaySearchTerm) {
      const term = holidaySearchTerm.toLowerCase();
      filtered = (filtered || []).filter(lead =>
        (lead.first_name?.toLowerCase().includes(term)) ||
        (lead.last_name?.toLowerCase().includes(term)) ||
        (lead.email?.toLowerCase().includes(term)) ||
        (lead.phone?.toLowerCase().includes(term))
      );
    }

    // Location filter
    if (locationFilter && locationFilter !== 'all') {
      filtered = (filtered || []).filter(lead => lead.location === locationFilter);
    }

    // Interest type filter
    if (interestFilter && interestFilter !== 'all') {
      filtered = (filtered || []).filter(lead => lead.interest_type === interestFilter);
    }

    // Consent filter
    if (consentFilter && consentFilter !== 'all') {
      const consentValue = consentFilter === 'opted-in';
      filtered = (filtered || []).filter(lead => lead.consent_marketing === consentValue);
    }

    setFilteredHolidayLeads(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [holidayLeads, holidaySearchTerm, locationFilter, interestFilter, consentFilter]);

  // Initialize Lenis smooth scroll
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smooth: true,
      smoothTouch: false,
    });

    lenisRef.current = lenis;

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  const fetchLeads = async () => {
    setIsLoadingLeads(true);
    setLeadsError('');
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/admin/leads?exclude_holiday=true`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setLeads(data.leads || []);
      } else if (response.status === 401) {
        handleLogout();
        toast.error('Session expired. Please login again.');
      } else {
        const msg = `Server returned ${response.status}`;
        setLeadsError(msg);
        toast.error('Failed to fetch leads');
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
      setLeadsError(error.message || 'Network error');
      toast.error('Network error. Please check your connection.');
    } finally {
      setIsLoadingLeads(false);
    }
  };

  const fetchHolidayLeads = async () => {
    setIsLoadingHolidayLeads(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/admin/leads?lead_type=holiday`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setHolidayLeads(data.leads || []);
      } else if (response.status === 401) {
        handleLogout();
        toast.error('Session expired. Please login again.');
      } else {
        toast.error('Failed to fetch holiday leads');
      }
    } catch (error) {
      console.error('Error fetching holiday leads:', error);
      toast.error('Network error. Please check your connection.');
    } finally {
      setIsLoadingHolidayLeads(false);
    }
  };

  // Fetch LetsTalkTaxes bookings
  const fetchLttBookings = async () => {
    setIsLoadingLtt(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/admin/letstalktaxes-bookings`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setLttBookings(data.bookings || []);
        setTotalLttBookings(data.total || 0);
      } else if (response.status === 401) {
        handleLogout();
        toast.error('Session expired. Please login again.');
      } else {
        toast.error('Failed to fetch LetsTalkTaxes bookings');
      }
    } catch (error) {
      console.error('Error fetching LTT bookings:', error);
      toast.error('Network error. Please check your connection.');
    } finally {
      setIsLoadingLtt(false);
    }
  };

  // Fetch referrals
  const fetchReferrals = async () => {
    setIsLoadingReferrals(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/admin/referrals`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });

      if (response.ok) {
        const data = await response.json();
        setReferrals(data.referrals || []);
        setTotalReferrals(data.total || 0);
      } else if (response.status === 401) {
        handleLogout();
        toast.error('Session expired. Please login again.');
      } else {
        toast.error('Failed to fetch referrals');
      }
    } catch (error) {
      console.error('Error fetching referrals:', error);
      toast.error('Network error.');
    } finally {
      setIsLoadingReferrals(false);
    }
  };

  // Fetch referrals when Referrals tab is active
  useEffect(() => {
    if (isAuthenticated && sessionToken && activeTab === 'referrals') {
      fetchReferrals();
    }
  }, [isAuthenticated, sessionToken, activeTab]);

  // Fetch pre-registrations
  const fetchPreRegistrations = async () => {
    setIsLoadingPreReg(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/admin/pre-registrations`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPreRegistrations(data.preregistrations || []);
      }
    } catch (error) {
      console.error('Error fetching pre-registrations:', error);
    } finally {
      setIsLoadingPreReg(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && sessionToken && activeTab === 'preregister') {
      fetchPreRegistrations();
    }
  }, [isAuthenticated, sessionToken, activeTab]);

  // Fetch coffee leads
  const fetchCoffeeLeads = async () => {
    setIsLoadingCoffee(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/admin/coffee-leads`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCoffeeLeads(data.leads || []);
      }
    } catch (error) {
      console.error('Error fetching coffee leads:', error);
    } finally {
      setIsLoadingCoffee(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && sessionToken && activeTab === 'coffee') {
      fetchCoffeeLeads();
    }
  }, [isAuthenticated, sessionToken, activeTab]);

  // Fetch LTT-KGOB leads
  const fetchLttKgobLeads = async () => {
    setIsLoadingLttKgob(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/admin/ltt-kgob-leads`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLttKgobLeads(data.leads || []);
        setLttKgobStats(data.stats || { new: 0, contacted: 0, converted: 0 });
      }
    } catch (error) {
      console.error('Error fetching LTT-KGOB leads:', error);
    } finally {
      setIsLoadingLttKgob(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && sessionToken && activeTab === 'lttkgob') {
      fetchLttKgobLeads();
    }
  }, [isAuthenticated, sessionToken, activeTab]);

  const fetchContacts = async () => {
    setIsLoadingContacts(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/admin/contacts`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setContacts(data.contacts || []);
      } else if (response.status === 401) {
        handleLogout();
        toast.error('Session expired. Please login again.');
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setIsLoadingContacts(false);
    }
  };

  const fetchTeamMembersList = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/admin/team/members`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTeamMembersList(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching team members list:', error);
    }
  };

  const handleAssignLead = async (leadId, assignedTo) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/admin/leads/${leadId}/assign`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ assigned_to: assignedTo || null })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Lead assigned successfully', {
          description: assignedTo ? 'Team member will now see this lead' : 'Lead unassigned'
        });
        fetchLeads(); // Refresh leads list
      } else {
        const error = await response.json();
        toast.error('Failed to assign lead', {
          description: error.detail || 'Please try again'
        });
      }
    } catch (error) {
      console.error('Error assigning lead:', error);
      toast.error('Failed to assign lead', {
        description: 'Network error'
      });
    }
  };


  const fetchTeamMembers = async () => {
    if (!isAdmin(currentUser)) return; // Only admin can fetch team members
    
    setIsLoadingTeam(true);
    setTeamError(''); // Clear previous errors
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/admin/team/members`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTeamMembers(Array.isArray(data) ? data : []);
        setTeamError(''); // Clear error on success
      } else if (response.status === 401) {
        handleLogout();
        toast.error('Session expired. Please login again.');
      } else if (response.status === 403) {
        const errorMsg = 'You do not have permission to view team members.';
        setTeamError(errorMsg);
        toast.error(errorMsg);
      } else {
        const errorMsg = 'Failed to load team members. Please try again.';
        setTeamError(errorMsg);
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
      const errorMsg = 'Failed to load team members. Please check your connection.';
      setTeamError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoadingTeam(false);
    }
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();
    setIsSendingInvite(true);
    setInviteError(''); // Clear previous errors

    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/admin/team/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify({
          email: inviteEmail,
          name: inviteName,
          permissions: invitePermissions
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success(`Invitation sent to ${inviteEmail}!`, {
          description: 'The team member will receive an email with registration instructions.'
        });
        setShowInviteModal(false);
        setInviteEmail('');
        setInviteName('');
        setInvitePermissions({
          leads: { view: false, edit: false, delete: false, export: false, add_comments: false },
          consultations: { view: false, edit: false, approve_reject: false, delete: false, add_comments: false },
          analytics: { view_own: false, view_team: false },
        });
        setInviteError('');
        fetchTeamMembers(); // Refresh team list
      } else {
        const errorMsg = data.message || data.detail || 'Failed to send invitation';
        setInviteError(errorMsg);
        toast.error('Invitation failed', {
          description: errorMsg
        });
      }
    } catch (error) {
      console.error('Error sending invitation:', error);
      const errorMsg = 'Network error. Please check your connection and try again.';
      setInviteError(errorMsg);
      toast.error('Invitation failed', {
        description: errorMsg
      });
    } finally {
      setIsSendingInvite(false);
    }
  };

  const togglePermission = (resource, action) => {
    setInvitePermissions(prev => ({
      ...prev,
      [resource]: {
        ...prev[resource],
        [action]: !prev[resource][action]
      }
    }));
  };

  const toggleEditPermission = (category, permission) => {
    setEditPermissions(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [permission]: !prev[category][permission]
      }
    }));
  };

  const handleEditMember = (member) => {
    setEditingMember(member);
    setEditName(member.name);
    setEditEmail(member.email);
    setEditPermissions(member.permissions);
    setShowEditModal(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setIsSavingEdit(true);
    setEditError(''); // Clear previous errors

    try {
      const sessionToken = localStorage.getItem('session_token');
      
      // Update profile (name/email)
      const profileResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/admin/team/members/${editingMember.member_id}/profile`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify({
          name: editName,
          email: editEmail
        })
      });

      if (!profileResponse.ok) {
        const error = await profileResponse.json();
        throw new Error(error.detail || error.message || 'Failed to update profile');
      }

      // Update permissions
      const permissionsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/admin/team/members/${editingMember.member_id}/permissions`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify(editPermissions)
      });

      if (!permissionsResponse.ok) {
        const error = await permissionsResponse.json();
        throw new Error(error.detail || error.message || 'Failed to update permissions');
      }

      toast.success('Team member updated successfully!', {
        description: `${editName}'s profile and permissions have been updated.`
      });
      setShowEditModal(false);
      setEditingMember(null);
      setEditName('');
      setEditEmail('');
      setEditPermissions({
        leads: { view: false, edit: false, delete: false, export: false, add_comments: false },
        consultations: { view: false, edit: false, approve_reject: false, delete: false, add_comments: false },
        analytics: { view_own: false, view_team: false },
      });
      setEditError('');
      
      // Refresh team list
      fetchTeamMembers();
    } catch (error) {
      console.error('Error updating team member:', error);
      const errorMsg = error.message || 'Failed to update team member. Please try again.';
      setEditError(errorMsg);
      toast.error('Update failed', {
        description: errorMsg
      });
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    setLoginError(''); // Clear previous errors

    try {
      // Try admin login first (username-based), fallback to team login (email-based)
      const isEmail = username.includes('@');
      const endpoint = isEmail ? '/api/v1/team/login' : '/api/v1/admin/login';
      const requestBody = isEmail 
        ? { email: username, password }
        : { username, password };
      
      const response = await fetch(`${BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (data.success && data.session_token) {
        setSessionToken(data.session_token);
        localStorage.setItem('leadboard_session_token', data.session_token);
        
        // Store user info for team members
        if (data.user) {
          localStorage.setItem('leadboard_user_info', JSON.stringify(data.user));
          setCurrentUser(data.user);
        } else {
          // Admin user - store basic info
          const adminUser = { role: 'admin', username: username };
          localStorage.setItem('leadboard_user_info', JSON.stringify(adminUser));
          setCurrentUser(adminUser);
        }
        
        setIsAuthenticated(true);
        setLoginError('');
        toast.success('Login successful!');
      } else {
        setLoginError(data.message || 'Invalid credentials. Please try again.');
        toast.error(data.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      setLoginError('Connection error. Please check your internet connection and try again.');
      toast.error('Connection error. Please try again.');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/v1/admin/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        }
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('leadboard_session_token');
      localStorage.removeItem('leadboard_user_info');
      setSessionToken('');
      setCurrentUser(null);
      setIsAuthenticated(false);
      setLeads([]);
      setContacts([]);
      setUsername('');
      setPassword('');
      toast.success('Logged out successfully');
    }
  };

  const handleAddContact = async (e) => {
    e.preventDefault();
    setIsAddingContact(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/admin/contacts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify({
          name: newContactName,
          email: newContactEmail,
          role: newContactRole || 'Team Member'
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Contact added successfully');
        setShowAddContactModal(false);
        setNewContactName('');
        setNewContactEmail('');
        setNewContactRole('');
        fetchContacts();
      } else {
        toast.error(data.detail || 'Failed to add contact');
      }
    } catch (error) {
      console.error('Error adding contact:', error);
      toast.error('Network error. Please try again.');
    } finally {
      setIsAddingContact(false);
    }
  };

  const handleDeleteContact = async (contactId, contactName) => {
    if (!window.confirm(`Are you sure you want to delete ${contactName}?`)) {
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/admin/contacts/${contactId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        }
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Contact deleted successfully');
        fetchContacts();
      } else {
        toast.error('Failed to delete contact');
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Network error. Please try again.');
    }
  };

  const handlePresetChange = (preset) => {
    setSelectedPreset(preset);
    if (preset && FIELD_PRESETS[preset]) {
      setSelectedFields(FIELD_PRESETS[preset].fields);
    }
  };

  const toggleField = (fieldValue) => {
    if (selectedFields.includes(fieldValue)) {
      setSelectedFields(selectedFields.filter(f => f !== fieldValue));
    } else {
      setSelectedFields([...selectedFields, fieldValue]);
    }
    // Clear preset if manually changing fields
    setSelectedPreset('');
  };

  const handleShareLead = async () => {
    if (!shareContactEmail) {
      toast.error('Please select a contact');
      return;
    }

    if (selectedFields.length === 0) {
      toast.error('Please select at least one field to share');
      return;
    }

    setIsSharingLead(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/admin/share-lead`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify({
          lead_id: leadToShare._id,
          contact_email: shareContactEmail,
          fields: selectedFields,
          preset: selectedPreset || null
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success(`Lead shared with ${shareContactEmail}`);
        setShowShareLeadModal(false);
        setLeadToShare(null);
        setShareContactEmail('');
        setSelectedFields([]);
        setSelectedPreset('');
      } else {
        toast.error(data.detail || 'Failed to share lead');
      }
    } catch (error) {
      console.error('Error sharing lead:', error);
      toast.error('Network error. Please try again.');
    } finally {
      setIsSharingLead(false);
    }
  };

  const openShareModal = (lead) => {
    setLeadToShare(lead);
    setShowShareLeadModal(true);
    setSelectedFields([]);
    setSelectedPreset('');
    setShareContactEmail('');
  };

  const handleBulkDelete = async () => {
    if (selectedLeads.length === 0) {
      toast.error('Please select leads to delete');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedLeads.length} lead(s)? This action cannot be undone.`)) {
      return;
    }

    setIsDeletingBulk(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/admin/leads/bulk`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify(selectedLeads)
      });

      const data = await response.json();

      if (data.success) {
        toast.success(`${data.deleted_count} lead(s) deleted successfully`);
        setSelectedLeads([]);
        fetchLeads();
      } else {
        toast.error(data.detail || 'Failed to delete leads');
      }
    } catch (error) {
      console.error('Error deleting leads:', error);
      toast.error('Network error. Please try again.');
    } finally {
      setIsDeletingBulk(false);
    }
  };

  const handleExportLeads = async (selectedFields) => {
    setIsExporting(true);

    try {
      // Build filters based on current view
      const filters = {};
      if (statusFilter !== 'all') {
        filters.status = statusFilter;
      }
      if (gradeFilter !== 'all') {
        filters.grade = gradeFilter;
      }

      const response = await fetch(`${BACKEND_URL}/api/v1/admin/leads/export`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify({ filters })
      });

      if (response.status === 429) {
        const data = await response.json();
        toast.error(data.detail || 'Rate limit exceeded. Please wait before exporting again.');
        setIsExporting(false);
        setShowExportModal(false);
        return;
      }

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Export failed');
      }

      // Get CSV content
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leads_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success(`Successfully exported ${selectedFields.length} fields`);
      setShowExportModal(false);
    } catch (error) {
      console.error('Error exporting leads:', error);
      toast.error(error.message || 'Failed to export leads. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const toggleLeadSelection = (leadId) => {
    if (selectedLeads.includes(leadId)) {
      setSelectedLeads(selectedLeads.filter(id => id !== leadId));
    } else {
      setSelectedLeads([...selectedLeads, leadId]);
    }
  };

  const toggleSelectAll = () => {
    if (selectedLeads.length === paginatedLeads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads((paginatedLeads || []).map(lead => lead._id));
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setGradeFilter('all');
    setDateRange('');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getGradeBadgeColor = (grade) => {
    switch (grade) {
      case 'A': return 'bg-green-500';
      case 'B': return 'bg-blue-500';
      case 'C': return 'bg-orange-500';
      case 'D': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'qualified': return 'bg-green-100 text-green-800';
      case 'verified': return 'bg-blue-100 text-blue-800';
      case 'initiated': return 'bg-yellow-100 text-yellow-800';
      case 'invalid_email': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const hasActiveFilters = searchTerm || statusFilter !== 'all' || gradeFilter !== 'all' || !!dateRange;

  // Calculate pagination
  const totalPages = Math.ceil(filteredLeads.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedLeads = filteredLeads.slice(startIndex, endIndex);

  // Calculate stats
  const totalLeads = leads.length;
  const qualifiedLeads = leads.filter(l => l.grade === 'A' || l.grade === 'B').length;
  const verifiedLeads = leads.filter(l => l.email_verified).length;
  const showingLeads = paginatedLeads.length;

  // Notification counts driven by the Overview endpoint (refreshed on tab change).
  // NOTE: This hook MUST be declared before the `if (!isAuthenticated) return ...`
  // early return below — otherwise React errors with "Rendered more hooks than
  // during the previous render" as soon as the user logs in.
  const [notificationCounts, setNotificationCounts] = useState({
    leads: 0, gya_queue: 0, indexing: 0, alerts: 0,
  });

  useEffect(() => {
    let alive = true;
    if (!sessionToken) return;
    const load = async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/v1/admin/overview/today`, {
          headers: { Authorization: `Bearer ${sessionToken}` },
        });
        if (!r.ok) return;
        const json = await r.json();
        if (alive && json?.notificationCounts) {
          setNotificationCounts((prev) => ({ ...prev, ...json.notificationCounts }));
        }
      } catch (_e) { /* silent — sidebar badges are best-effort */ }
    };
    load();
    // Refresh every 60s while authenticated
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [sessionToken]);

  // Login Screen — re-skinned with the v2 teal+navy palette (Phase 1).
  if (!isAuthenticated) {
    return (
      <div className="lb2">
        <div className="login-screen" data-testid="login">
          <div style={{ width: '100%', maxWidth: 400 }}>
            <div className="login-card">
              <img src="/leadboard/kg-bubble.png" alt="KG" className="login-logo-img" />
              <h2 className="login-title">Leadboard Admin</h2>
              <p className="login-sub">Sign in to continue</p>
              <form data-testid="lead-board-form-3" onSubmit={handleLogin}>
                <label className="login-label" htmlFor="lb2-username">Username or Email</label>
                <input
                  id="lb2-username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="login-input"
                  data-testid="login-username-input"
                  placeholder="Enter your username or email"
                  required
                  style={{ marginBottom: 14 }}
                />
                <label className="login-label" htmlFor="lb2-password">Password</label>
                <input
                  id="lb2-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="login-input"
                  data-testid="login-password-input"
                  placeholder="Enter your password"
                  required
                  style={{ marginBottom: 18 }}
                />
                {loginError && (
                  <div
                    role="alert"
                    style={{
                      background: 'var(--bad-soft)',
                      color: 'var(--bad)',
                      border: '1px solid var(--bad)',
                      borderRadius: 10,
                      padding: '10px 12px',
                      marginBottom: 14,
                      fontSize: 13,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <AlertCircle className="w-4 h-4" />
                    {loginError}
                  </div>
                )}
                <button
                  type="submit"
                  className="login-submit"
                  disabled={isLoggingIn}
                  data-testid="login-button"
                >
                  {isLoggingIn ? 'Signing in…' : 'Sign In'}
                </button>
              </form>
              <p style={{ textAlign: 'center', marginTop: 14, color: 'var(--muted)', fontSize: 12 }}>
                Team members: use your email address to sign in
              </p>
            </div>
            <p className="login-tagline">Let&apos;s Talk Growth®</p>
          </div>
        </div>
      </div>
    );
  }

  // Resolve activeTab against role gating
  const safeTab = isTabAllowed(activeTab, currentUser?.role) ? activeTab : defaultTabFor(currentUser?.role);

  // Dashboard Screen — wrapped in the new LeadboardShell (Phase 1).
  return (
    <LeadboardShell
      user={{ name: currentUser?.full_name || currentUser?.email || username, email: currentUser?.email || username }}
      role={currentUser?.role}
      activeTab={safeTab}
      onTabChange={setActiveTab}
      onLogout={handleLogout}
      notificationCounts={notificationCounts}
    >
      {/* New Phase 1 tabs (placeholders for Phase 2/3) */}
      {safeTab === 'overview' && (
        <Overview
          onNav={setActiveTab}
          user={{ name: currentUser?.full_name || currentUser?.email || username, email: currentUser?.email || username }}
          sessionToken={sessionToken}
        />
      )}
      {safeTab === 'bridge' && currentUser?.role === 'admin' && <BridgePlaceholder />}
      {safeTab === 'billing' && currentUser?.role === 'admin' && <BillingTab sessionToken={sessionToken} />}

      {/* === EXISTING TAB CONTENT below — preserved verbatim during Phase 1 === */}
      {/* Tab Content (gold-pill tab strip removed — driven by sidebar via LeadboardShell now) */}
      {/* Original markup below retained from the legacy LeadBoard.js shell. */}
        {/* Tab Content */}
        {activeTab === 'leads' && (
          <LeadsTab
            leads={leads}
            filteredLeads={filteredLeads}
            paginatedLeads={paginatedLeads}
            isLoadingLeads={isLoadingLeads}
            leadsError={leadsError}
            onRetryLeads={fetchLeads}
            totalLeads={totalLeads}
            qualifiedLeads={qualifiedLeads}
            showingLeads={showingLeads}
            verifiedLeads={verifiedLeads}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            gradeFilter={gradeFilter}
            setGradeFilter={setGradeFilter}
            dateRange={dateRange}
            setDateRange={setDateRange}
            clearFilters={clearFilters}
            hasActiveFilters={hasActiveFilters}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            pageSize={itemsPerPage}
            setPageSize={setItemsPerPage}
            selectedLeads={selectedLeads}
            setSelectedLeads={setSelectedLeads}
            currentUser={currentUser}
            canExportFn={canExport}
            canDeleteFn={canDelete}
            teamMembers={teamMembersList.filter((m) => m.role === 'member')}
            isExporting={isExporting}
            isDeletingBulk={isDeletingBulk}
            isSharing={isSharingLead}
            isAdding={isAddingContact}
            onView={(id) => {
              const lead = leads.find((l) => l._id === id);
              navigate(`/leadboard/leads/${id}`, { state: { lead } });
            }}
            onShare={async ({ leadId, recipientEmail, fields, note }) => {
              try {
                setIsSharingLead(true);
                const res = await fetch(`${BACKEND_URL}/api/v1/admin/share-lead`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${sessionToken}` },
                  body: JSON.stringify({ lead_id: leadId, contact_email: recipientEmail, selected_fields: fields, note: note || '' }),
                });
                const data = await res.json();
                if (data.success) toast.success('Lead shared');
                else toast.error(data.detail || 'Failed to share lead');
              } catch (e) { toast.error('Network error. Please try again.'); }
              finally { setIsSharingLead(false); }
            }}
            onExport={async ({ format, fields, exportType }) => {
              try {
                setIsExporting(true);
                const params = new URLSearchParams();
                params.set('export_format', format || 'csv');
                params.set('export_type', exportType || 'business');
                if (Array.isArray(fields) && fields.length > 0) params.set('fields', fields.join(','));
                if (statusFilter !== 'all') params.set('status', statusFilter);
                if (gradeFilter !== 'all') params.set('grade', gradeFilter);
                const res = await fetch(`${BACKEND_URL}/api/v1/admin/leads/export?${params.toString()}`, {
                  headers: { Authorization: `Bearer ${sessionToken}` },
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url; a.download = `leads-${exportType}-${new Date().toISOString().slice(0, 10)}.${format || 'csv'}`;
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
                toast.success('Export downloaded');
              } catch (e) { toast.error('Export failed'); }
              finally { setIsExporting(false); }
            }}
            onAddContact={async (form) => {
              try {
                setIsAddingContact(true);
                // Adapter: AddContactInline collects {first_name,last_name,email,phone,business_name,source,status,notes}
                // Existing /admin/contacts expects {name,email,role}. Map appropriately.
                const fullName = `${form.first_name} ${form.last_name}`.trim();
                const res = await fetch(`${BACKEND_URL}/api/v1/admin/contacts`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${sessionToken}` },
                  body: JSON.stringify({ name: fullName, email: form.email, role: form.source === 'manual' ? 'Team Member' : form.source }),
                });
                const data = await res.json();
                if (data.success) { toast.success('Contact added'); fetchContacts(); }
                else toast.error(data.detail || 'Failed to add contact');
              } catch (e) { toast.error('Network error. Please try again.'); }
              finally { setIsAddingContact(false); }
            }}
            onBulkDelete={async ({ reason }) => {
              try {
                setIsDeletingBulk(true);
                const res = await fetch(`${BACKEND_URL}/api/v1/admin/leads/bulk-delete`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${sessionToken}` },
                  body: JSON.stringify({ lead_ids: selectedLeads, reason: reason || '' }),
                });
                const data = await res.json();
                if (data.success) {
                  toast.success(`Deleted ${selectedLeads.length} lead(s)`);
                  setSelectedLeads([]);
                  fetchLeads();
                } else toast.error(data.detail || 'Bulk delete failed');
              } catch (e) { toast.error('Network error. Please try again.'); }
              finally { setIsDeletingBulk(false); }
            }}
          />
        )}

        
        {activeTab === 'holiday' && (
          <>
          {/* Holiday Leads Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card className="bg-ivory">
              <CardContent className="pt-6">
                <div className="text-sm text-gray-600 mb-1">Total Holiday Leads</div>
                <div className="text-3xl font-bold text-navy">{holidayLeads.length}</div>
              </CardContent>
            </Card>
            <Card className="bg-ivory">
              <CardContent className="pt-6">
                <div className="text-sm text-gray-600 mb-1">Opted-In</div>
                <div className="text-3xl font-bold text-green-600">
                  {holidayLeads.filter(l => l.consent_marketing).length}
                </div>
              </CardContent>
            </Card>
            <Card className="bg-ivory">
              <CardContent className="pt-6">
                <div className="text-sm text-gray-600 mb-1">Business Owners</div>
                <div className="text-3xl font-bold text-blue-600">
                  {holidayLeads.filter(l => l.interest_type === 'Business Owner').length}
                </div>
              </CardContent>
            </Card>
            <Card className="bg-ivory">
              <CardContent className="pt-6">
                <div className="text-sm text-gray-600 mb-1">PDF Downloads</div>
                <div className="text-3xl font-bold text-purple-600">
                  {holidayLeads.filter(l => l.pdf_downloaded).length}
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Export Button for Admin */}
          {isAdmin(currentUser) && holidayLeads.length > 0 && (
            <div className="mb-6 flex justify-between items-center">
              <div>
                {selectedHolidayLeads.length > 0 && (
                  <Button data-testid="lead-board-button-10"
                    onClick={async () => {
                      if (!window.confirm(`Delete ${selectedHolidayLeads.length} holiday lead(s)? This cannot be undone.`)) return;
                      
                      try {
                        const response = await fetch(`${BACKEND_URL}/api/v1/admin/leads/bulk`, {
                          method: 'DELETE',
                          headers: {
                            'Authorization': `Bearer ${sessionToken}`,
                            'Content-Type': 'application/json'
                          },
                          body: JSON.stringify(selectedHolidayLeads)
                        });
                        
                        if (response.ok) {
                          toast.success(`${selectedHolidayLeads.length} lead(s) deleted`);
                          setSelectedHolidayLeads([]);
                          fetchHolidayLeads();
                        } else {
                          toast.error('Failed to delete leads');
                        }
                      } catch (error) {
                        console.error('Bulk delete error:', error);
                        toast.error('Delete failed');
                      }
                    }}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete {selectedHolidayLeads.length} Selected
                  </Button>
                )}
              </div>
              <Button data-testid="lead-board-button-9"
                onClick={async () => {
                  try {
                    const response = await fetch(`${BACKEND_URL}/api/v1/admin/leads/export?lead_type=holiday`, {
                      headers: {
                        'Authorization': `Bearer ${sessionToken}`
                      }
                    });
                    
                    if (response.ok) {
                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `holiday-leads-${new Date().toISOString().split('T')[0]}.csv`;
                      document.body.appendChild(a);
                      a.click();
                      window.URL.revokeObjectURL(url);
                      document.body.removeChild(a);
                      toast.success('Holiday leads exported successfully');
                    } else {
                      toast.error('Failed to export holiday leads');
                    }
                  } catch (error) {
                    console.error('Export error:', error);
                    toast.error('Export failed');
                  }
                }}
                className="bg-[#D4AF37] hover:bg-[#B99610] text-white"
              >
                <Download className="w-4 h-4 mr-2" />
                Export Holiday Leads
              </Button>
            </div>
          )}

          {/* Holiday Filters */}
          <Card className="bg-ivory mb-8">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 mb-4">
                <Filter className="w-5 h-5 text-navy" />
                <h3 className="text-navy font-semibold">Filters</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Search */}
                <div>
                  <Label className="text-sm text-gray-600">Search</Label>
                  <Input data-testid="lead-board-input-1"
                    placeholder="Name, email, or phone..."
                    value={holidaySearchTerm}
                    onChange={(e) => setHolidaySearchTerm(e.target.value)}
                    className="focus:ring-gold focus:border-gold"
                  />
                </div>
                
                {/* Location Filter */}
                <div>
                  <Label className="text-sm text-gray-600">Location</Label>
                  <Select data-testid="lead-board-select-3" value={locationFilter} onValueChange={setLocationFilter}>
                    <SelectTrigger data-testid="lead-board-selecttrigger-3" className="focus:ring-gold">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem data-testid="lead-board-selectitem-11" value="all">All Locations</SelectItem>
                      <SelectItem data-testid="lead-board-selectitem-10" value="Winston-Salem">Winston-Salem</SelectItem>
                      <SelectItem data-testid="lead-board-selectitem-9" value="Charlotte">Charlotte</SelectItem>
                      <SelectItem data-testid="lead-board-selectitem-8" value="Mooresville">Mooresville</SelectItem>
                      <SelectItem data-testid="lead-board-selectitem-7" value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Interest Filter */}
                <div>
                  <Label className="text-sm text-gray-600">Interest Type</Label>
                  <Select data-testid="lead-board-select-2" value={interestFilter} onValueChange={setInterestFilter}>
                    <SelectTrigger data-testid="lead-board-selecttrigger-2" className="focus:ring-gold">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem data-testid="lead-board-selectitem-6" value="all">All Types</SelectItem>
                      <SelectItem data-testid="lead-board-selectitem-5" value="Business Owner">Business Owner</SelectItem>
                      <SelectItem data-testid="lead-board-selectitem-4" value="Individual/Family">Individual/Family</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Consent Filter */}
                <div>
                  <Label className="text-sm text-gray-600">Consent Status</Label>
                  <Select data-testid="lead-board-select-1" value={consentFilter} onValueChange={setConsentFilter}>
                    <SelectTrigger data-testid="lead-board-selecttrigger-1" className="focus:ring-gold">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem data-testid="lead-board-selectitem-3" value="all">All</SelectItem>
                      <SelectItem data-testid="lead-board-selectitem-2" value="opted-in">Opted-In</SelectItem>
                      <SelectItem data-testid="lead-board-selectitem-1" value="opted-out">Opted-Out</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Holiday Leads Table */}
          <Card className="bg-ivory">
            <CardHeader>
              <CardTitle className="text-navy">
                Holiday Leads {isLoadingHolidayLeads && <span className="text-sm text-gray-500 ml-2">(Loading...)</span>}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {filteredHolidayLeads.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No holiday leads found.
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="holiday-leads-table">
                      <thead>
                        <tr className="border-b border-gray-200">
                          {isAdmin(currentUser) && (
                            <th className="py-3 px-4">
                              <input data-testid="lead-board-input-2"
                                type="checkbox"
                                checked={selectedHolidayLeads.length === filteredHolidayLeads.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).length && filteredHolidayLeads.length > 0}
                                onChange={() => {
                                  const pageLeads = filteredHolidayLeads.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
                                  if (selectedHolidayLeads.length === pageLeads.length) {
                                    setSelectedHolidayLeads([]);
                                  } else {
                                    setSelectedHolidayLeads((pageLeads || []).map(lead => lead._id));
                                  }
                                }}
                                className="w-4 h-4 rounded border-gray-300"
                              />
                            </th>
                          )}
                          <th className="text-left py-3 px-4 text-navy font-semibold">Title</th>
                          <th className="text-left py-3 px-4 text-navy font-semibold">Name</th>
                          <th className="text-left py-3 px-4 text-navy font-semibold">Email</th>
                          <th className="text-left py-3 px-4 text-navy font-semibold">Phone</th>
                          <th className="text-left py-3 px-4 text-navy font-semibold">Consent</th>
                          <th className="text-left py-3 px-4 text-navy font-semibold">Interest</th>
                          <th className="text-left py-3 px-4 text-navy font-semibold">Location</th>
                          <th className="text-left py-3 px-4 text-navy font-semibold">Source</th>
                          <th className="text-left py-3 px-4 text-navy font-semibold">Created</th>
                          {isAdmin(currentUser) && (
                            <th className="text-left py-3 px-4 text-navy font-semibold">Actions</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredHolidayLeads.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((lead) => (
                          <tr
                            key={lead._id}
                            className="border-b border-gray-100 hover:bg-gray-50"
                            data-testid={`holiday-lead-row-${lead._id}`}
                          >
                            {isAdmin(currentUser) && (
                              <td className="py-3 px-4">
                                <input data-testid="lead-board-input-1"
                                  type="checkbox"
                                  checked={selectedHolidayLeads.includes(lead._id)}
                                  onChange={() => {
                                    if (selectedHolidayLeads.includes(lead._id)) {
                                      setSelectedHolidayLeads(selectedHolidayLeads.filter(id => id !== lead._id));
                                    } else {
                                      setSelectedHolidayLeads([...selectedHolidayLeads, lead._id]);
                                    }
                                  }}
                                  className="w-4 h-4 rounded border-gray-300"
                                />
                              </td>
                            )}
                            <td className="py-3 px-4">
                              <div className="font-medium text-navy">{lead.salutation || '-'}</div>
                            </td>
                            <td className="py-3 px-4">
                              <div className="font-semibold text-navy">
                                {lead.first_name} {lead.last_name || ''}
                              </div>
                            </td>
                            <td className="py-3 px-4 text-gray-700">{lead.email}</td>
                            <td className="py-3 px-4 text-gray-700">{lead.phone}</td>
                            <td className="py-3 px-4">
                              {lead.consent_marketing ? (
                                <Badge className="bg-green-100 text-green-800">✓ Opted-In</Badge>
                              ) : (
                                <Badge className="bg-red-100 text-red-800">✗ Opted-Out</Badge>
                              )}
                            </td>
                            <td className="py-3 px-4 text-gray-700">{lead.interest_type || '-'}</td>
                            <td className="py-3 px-4 text-gray-700">{lead.location || '-'}</td>
                            <td className="py-3 px-4">
                              <div className="text-xs">
                                <div className="font-medium text-gray-700">{lead.source || '-'}</div>
                                <div className="text-gray-500">{lead.campaign || '-'}</div>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-600">
                              {lead.created_at ? new Date(lead.created_at).toLocaleDateString() : '-'}
                            </td>
                            {isAdmin(currentUser) && (
                              <td className="py-3 px-4">
                                <Button data-testid="lead-board-button-8"
                                  variant="ghost"
                                  size="sm"
                                  onClick={async () => {
                                    if (window.confirm(`Delete ${lead.first_name} ${lead.last_name}? This cannot be undone.`)) {
                                      try {
                                        const response = await fetch(`${BACKEND_URL}/api/v1/admin/leads/${lead._id}`, {
                                          method: 'DELETE',
                                          headers: {
                                            'Authorization': `Bearer ${sessionToken}`
                                          }
                                        });
                                        
                                        if (response.ok) {
                                          toast.success('Lead deleted successfully');
                                          fetchHolidayLeads(); // Refresh list
                                        } else {
                                          toast.error('Failed to delete lead');
                                        }
                                      } catch (error) {
                                        console.error('Delete error:', error);
                                        toast.error('Delete failed');
                                      }
                                    }
                                  }}
                                  className="text-red-600 hover:text-red-800 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {filteredHolidayLeads.length > itemsPerPage && (
                    <div className="flex justify-between items-center mt-6">
                      <div className="text-sm text-gray-600">
                        Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredHolidayLeads.length)} of {filteredHolidayLeads.length} leads
                      </div>
                      <div className="flex gap-2">
                        <Button data-testid="lead-board-button-7"
                          variant="outline"
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          className="border-navy text-navy hover:bg-navy hover:text-white"
                        >
                          <ChevronLeft className="w-4 h-4" />
                          Previous
                        </Button>
                        <Button data-testid="lead-board-button-6"
                          variant="outline"
                          onClick={() => setCurrentPage(p => p + 1)}
                          disabled={currentPage * itemsPerPage >= filteredHolidayLeads.length}
                          className="border-navy text-navy hover:bg-navy hover:text-white"
                        >
                          Next
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
          </>
        )}
        


        {/* LetsTalkTaxes Tab */}
        {activeTab === 'letstalktaxes' && (
          <Card className="border-2 border-[#E1DFD7]">
            <CardHeader className="border-b border-[#E1DFD7] pb-6">
              <CardTitle className="text-2xl font-['Playfair_Display'] text-[#0B2240]">
                LetsTalkTaxes Consultation Bookings
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-8">
              {isLoadingLtt ? (
                <div className="text-center py-12">
                  <p className="text-[#4C5A70]">Loading bookings...</p>
                </div>
              ) : (
                <>
                  {/* Stats Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    <Card className="border-2 border-[#E1DFD7]">
                      <CardContent className="p-6 text-center">
                        <div className="text-sm text-gray-600 mb-1">Pending Approval</div>
                        <div className="text-3xl font-bold text-[#D4AF37]">
                          {lttBookings.filter(b => b.status === 'pending_approval').length}
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border-2 border-[#E1DFD7]">
                      <CardContent className="p-6 text-center">
                        <div className="text-sm text-gray-600 mb-1">Approved</div>
                        <div className="text-3xl font-bold text-green-600">
                          {lttBookings.filter(b => b.status === 'approved').length}
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border-2 border-[#E1DFD7]">
                      <CardContent className="p-6 text-center">
                        <div className="text-sm text-gray-600 mb-1">Completed</div>
                        <div className="text-3xl font-bold text-[#0B2240]">
                          {lttBookings.filter(b => b.status === 'completed').length}
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border-2 border-[#E1DFD7]">
                      <CardContent className="p-6 text-center">
                        <div className="text-sm text-gray-600 mb-1">Rejected</div>
                        <div className="text-3xl font-bold text-red-600">
                          {lttBookings.filter(b => b.status === 'rejected').length}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Bookings Table */}
                  {lttBookings.length === 0 ? (
                    <div className="text-center py-12 bg-[#F7F6F2] rounded-lg">
                      <p className="text-[#4C5A70] mb-2">No bookings yet</p>
                      <p className="text-sm text-[#4C5A70]">
                        Bookings from kgob.com/letstalktaxes will appear here
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b-2 border-[#E1DFD7]">
                            <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B2240]">Date</th>
                            <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B2240]">Customer</th>
                            <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B2240]">Type</th>
                            <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B2240]">Topic</th>
                            <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B2240]">Appointment</th>
                            <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B2240]">Coupon</th>
                            <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B2240]">Status</th>
                            <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B2240]">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {lttBookings.map((booking) => (
                            <tr key={booking.id} className="border-b border-[#E1DFD7] hover:bg-[#F7F6F2]">
                              <td className="py-4 px-4 text-sm text-[#4C5A70]">
                                {new Date(booking.created_at).toLocaleDateString()}
                              </td>
                              <td className="py-4 px-4">
                                <div>
                                  <p className="text-sm font-medium text-[#0B2240]">{booking.customer_name}</p>
                                  <p className="text-xs text-[#4C5A70]">{booking.email}</p>
                                </div>
                              </td>
                              <td className="py-4 px-4">
                                {booking.client_type === 'business' ? (
                                  <div className="flex items-center gap-1 text-xs text-[#4C5A70]">
                                    <Building className="w-3 h-3" />
                                    <span>Business</span>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1 text-xs text-[#4C5A70]">
                                    <User className="w-3 h-3" />
                                    <span>Individual</span>
                                  </div>
                                )}
                              </td>
                              <td className="py-4 px-4 text-sm text-[#4C5A70]">
                                {booking.topic.replace('_', ' ')}
                              </td>
                              <td className="py-4 px-4 text-xs text-[#4C5A70]">
                                {booking.appointment_date}<br />
                                {booking.appointment_time}
                              </td>
                              <td className="py-4 px-4">
                                <div className="text-xs">
                                  <p className="font-mono text-[#4C5A70]">{booking.coupon_code}</p>
                                  {booking.coupon_redeemed && (
                                    <Badge className="bg-green-100 text-green-800 text-xs mt-1">Redeemed</Badge>
                                  )}
                                </div>
                              </td>
                              <td className="py-4 px-4">
                                <Badge className={
                                  booking.status === 'pending_approval' ? 'bg-yellow-100 text-yellow-800' :
                                  booking.status === 'approved' ? 'bg-green-100 text-green-800' :
                                  booking.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                                  booking.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                  'bg-gray-100 text-gray-800'
                                }>
                                  {booking.status.replace('_', ' ')}
                                </Badge>
                              </td>
                              <td className="py-4 px-4">
                                <Button data-testid="lead-board-button-5"
                                  size="sm"
                                  variant="outline"
                                  className="text-xs"
                                  onClick={() => navigate(`/leadboard/letstalktaxes/${booking.id}`)}
                                >
                                  View
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        )}



        {/* Referrals Tab */}
        {activeTab === 'referrals' && (
          <Card className="border-2 border-[#E1DFD7]">
            <CardHeader className="border-b border-[#E1DFD7] pb-6">
              <CardTitle className="text-2xl font-['Playfair_Display'] text-[#0B2240]">
                Referrals
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-8">
              {isLoadingReferrals ? (
                <div className="text-center py-12">
                  <p className="text-[#4C5A70]">Loading referrals...</p>
                </div>
              ) : (
                <>
                  {/* Stats Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    <Card className="border-2 border-[#E1DFD7]">
                      <CardContent className="p-6 text-center">
                        <div className="text-sm text-gray-600 mb-1">New</div>
                        <div className="text-3xl font-bold text-[#D4AF37]">
                          {referrals.filter(r => r.status === 'new').length}
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border-2 border-[#E1DFD7]">
                      <CardContent className="p-6 text-center">
                        <div className="text-sm text-gray-600 mb-1">Contacted</div>
                        <div className="text-3xl font-bold text-blue-600">
                          {referrals.filter(r => r.status === 'contacted').length}
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border-2 border-[#E1DFD7]">
                      <CardContent className="p-6 text-center">
                        <div className="text-sm text-gray-600 mb-1">Qualified</div>
                        <div className="text-3xl font-bold text-green-600">
                          {referrals.filter(r => r.status === 'qualified').length}
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border-2 border-[#E1DFD7]">
                      <CardContent className="p-6 text-center">
                        <div className="text-sm text-gray-600 mb-1">Converted</div>
                        <div className="text-3xl font-bold text-[#0B2240]">
                          {referrals.filter(r => r.status === 'converted').length}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Referrals Table */}
                  {referrals.length === 0 ? (
                    <div className="text-center py-12 bg-[#F7F6F2] rounded-lg">
                      <p className="text-[#4C5A70] mb-2">No referrals yet</p>
                      <p className="text-sm text-[#4C5A70]">
                        Referrals from kgob.com/refer-us will appear here
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b-2 border-[#E1DFD7]">
                            <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B2240]">Date</th>
                            <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B2240]">Referred By</th>
                            <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B2240]">Referee</th>
                            <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B2240]">Type</th>
                            <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B2240]">Email Sent</th>
                            <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B2240]">Status</th>
                            <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B2240]">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {referrals.map((referral) => (
                            <tr key={referral.id} className="border-b border-[#E1DFD7] hover:bg-[#F7F6F2]">
                              <td className="py-4 px-4 text-sm text-[#4C5A70]">
                                {new Date(referral.created_at).toLocaleDateString()}
                              </td>
                              <td className="py-4 px-4">
                                <div>
                                  <p className="text-sm font-medium text-[#0B2240]">{referral.referrer_name}</p>
                                  <p className="text-xs text-[#4C5A70]">{referral.referrer_email}</p>
                                </div>
                              </td>
                              <td className="py-4 px-4">
                                <div>
                                  <p className="text-sm font-medium text-[#0B2240]">{referral.referee_name}</p>
                                  <p className="text-xs text-[#4C5A70]">{referral.referee_email}</p>
                                </div>
                              </td>
                              <td className="py-4 px-4">
                                {referral.lead_type === 'business' ? (
                                  <div className="flex items-center gap-1 text-xs text-[#4C5A70]">
                                    <Building className="w-3 h-3" />
                                    <span>Business</span>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1 text-xs text-[#4C5A70]">
                                    <User className="w-3 h-3" />
                                    <span>Individual</span>
                                  </div>
                                )}
                              </td>
                              <td className="py-4 px-4">
                                {referral.email_sent ? (
                                  <Badge className="bg-green-100 text-green-800 text-xs">Yes</Badge>
                                ) : (
                                  <Badge className="bg-gray-100 text-gray-800 text-xs">No</Badge>
                                )}
                              </td>
                              <td className="py-4 px-4">
                                <Badge className={
                                  referral.status === 'new' ? 'bg-yellow-100 text-yellow-800 text-xs' :
                                  referral.status === 'contacted' ? 'bg-blue-100 text-blue-800 text-xs' :
                                  referral.status === 'qualified' ? 'bg-green-100 text-green-800 text-xs' :
                                  referral.status === 'converted' ? 'bg-purple-100 text-purple-800 text-xs' :
                                  'bg-gray-100 text-gray-800 text-xs'
                                }>
                                  {referral.status}
                                </Badge>
                              </td>
                              <td className="py-4 px-4">
                                <Button data-testid="lead-board-button-4"
                                  size="sm"
                                  variant="outline"
                                  className="text-xs"
                                  onClick={() => navigate(`/leadboard/referrals/${referral.id}`)}
                                >
                                  View
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        )}



        {/* Pre-Register Tab */}
        {activeTab === 'preregister' && (
          <Card className="border-2 border-[#E1DFD7]">
            <CardHeader className="border-b border-[#E1DFD7] pb-6">
              <CardTitle className="text-2xl font-['Playfair_Display'] text-[#0B2240]">
                Client Portal Pre-Registrations
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-8">
              {isLoadingPreReg ? (
                <div className="text-center py-12"><p className="text-[#4C5A70]">Loading...</p></div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    <Card className="border-2 border-[#E1DFD7]"><CardContent className="p-6 text-center">
                      <div className="text-sm text-gray-600 mb-1">Pending</div>
                      <div className="text-3xl font-bold text-[#D4AF37]">{preRegistrations.filter(p => p.status === 'pending').length}</div>
                    </CardContent></Card>
                    <Card className="border-2 border-[#E1DFD7]"><CardContent className="p-6 text-center">
                      <div className="text-sm text-gray-600 mb-1">Invited</div>
                      <div className="text-3xl font-bold text-blue-600">{preRegistrations.filter(p => p.status === 'invited').length}</div>
                    </CardContent></Card>
                    <Card className="border-2 border-[#E1DFD7]"><CardContent className="p-6 text-center">
                      <div className="text-sm text-gray-600 mb-1">Registered</div>
                      <div className="text-3xl font-bold text-green-600">{preRegistrations.filter(p => p.status === 'registered').length}</div>
                    </CardContent></Card>
                    <Card className="border-2 border-[#E1DFD7]"><CardContent className="p-6 text-center">
                      <div className="text-sm text-gray-600 mb-1">Total</div>
                      <div className="text-3xl font-bold text-[#0B2240]">{preRegistrations.length}</div>
                    </CardContent></Card>
                  </div>
                  {preRegistrations.length === 0 ? (
                    <div className="text-center py-12 bg-[#F7F6F2] rounded-lg">
                      <p className="text-[#4C5A70]">No pre-registrations yet</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead><tr className="border-b-2 border-[#E1DFD7]">
                          <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B2240]">Date</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B2240]">Name</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B2240]">Email</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B2240]">Phone</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B2240]">Type</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B2240]">Status</th>
                        </tr></thead>
                        <tbody>
                          {preRegistrations.map((pr) => (
                            <tr key={pr.id} className="border-b border-[#E1DFD7] hover:bg-[#F7F6F2]">
                              <td className="py-4 px-4 text-sm text-[#4C5A70]">{new Date(pr.created_at).toLocaleDateString()}</td>
                              <td className="py-4 px-4 text-sm font-medium text-[#0B2240]">{pr.name}</td>
                              <td className="py-4 px-4 text-sm text-[#4C5A70]">{pr.email}</td>
                              <td className="py-4 px-4 text-sm text-[#4C5A70]">{pr.phone}</td>
                              <td className="py-4 px-4">{pr.client_type === 'business' ? <Building className="w-4 h-4" /> : <User className="w-4 h-4" />}</td>
                              <td className="py-4 px-4"><Badge className="bg-yellow-100 text-yellow-800 text-xs">{pr.status}</Badge></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Coffee Tab */}
        {activeTab === 'coffee' && (
          <Card className="border-2 border-[#E1DFD7]">
            <CardHeader className="border-b border-[#E1DFD7]">
              <CardTitle className="text-2xl font-['Playfair_Display'] text-[#0B2240]">Coffee Friday Walk-Ins</CardTitle>
            </CardHeader>
            <CardContent className="pt-8">
              {isLoadingCoffee ? (
                <div className="text-center py-12"><p>Loading...</p></div>
              ) : coffeeLeads.length === 0 ? (
                <div className="text-center py-12 bg-[#F7F6F2] rounded-lg">
                  <p className="text-[#4C5A70]">No coffee leads yet</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead><tr className="border-b-2 border-[#E1DFD7]">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B2240]">Date</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B2240]">Name</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B2240]">Email</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B2240]">Phone</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B2240]">Location</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B2240]">Status</th>
                  </tr></thead>
                  <tbody>
                    {coffeeLeads.map((lead) => (
                      <tr key={lead.id} className="border-b border-[#E1DFD7]">
                        <td className="py-4 px-4 text-sm text-[#4C5A70]">{new Date(lead.created_at).toLocaleString()}</td>
                        <td className="py-4 px-4 text-sm font-medium text-[#0B2240]">{lead.name}</td>
                        <td className="py-4 px-4 text-sm text-[#4C5A70]">{lead.email}</td>
                        <td className="py-4 px-4 text-sm text-[#4C5A70]" data-testid="coffee-lead-phone">{lead.phone ? <a data-testid="lead-board-a-1" href={`tel:${lead.phone}`} className="text-[#0B2240] hover:underline">{lead.phone}</a> : <span className="text-gray-400">—</span>}</td>
                        <td className="py-4 px-4"><Badge className="bg-blue-100 text-blue-800 text-xs">{lead.location}</Badge></td>
                        <td className="py-4 px-4"><Badge className="bg-yellow-100 text-yellow-800 text-xs">{lead.status}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        )}

        {/* LTT-KGOB Tab */}
        {activeTab === 'lttkgob' && (
          <Card className="border-2 border-[#E1DFD7]">
            <CardHeader className="border-b border-[#E1DFD7]">
              <CardTitle className="text-2xl font-['Playfair_Display'] text-[#0B2240]">
                LTT-KGOB Leads (Meet KGOB Landing Page)
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-8">
              {isLoadingLttKgob ? (
                <div className="text-center py-12"><p className="text-[#4C5A70]">Loading...</p></div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    <Card className="border-2 border-[#E1DFD7]"><CardContent className="p-6 text-center">
                      <div className="text-sm text-gray-600 mb-1">New Leads</div>
                      <div className="text-3xl font-bold text-[#D4AF37]">{lttKgobStats.new}</div>
                    </CardContent></Card>
                    <Card className="border-2 border-[#E1DFD7]"><CardContent className="p-6 text-center">
                      <div className="text-sm text-gray-600 mb-1">Contacted</div>
                      <div className="text-3xl font-bold text-blue-600">{lttKgobStats.contacted}</div>
                    </CardContent></Card>
                    <Card className="border-2 border-[#E1DFD7]"><CardContent className="p-6 text-center">
                      <div className="text-sm text-gray-600 mb-1">Converted</div>
                      <div className="text-3xl font-bold text-green-600">{lttKgobStats.converted}</div>
                    </CardContent></Card>
                    <Card className="border-2 border-[#E1DFD7]"><CardContent className="p-6 text-center">
                      <div className="text-sm text-gray-600 mb-1">Total</div>
                      <div className="text-3xl font-bold text-[#0B2240]">{lttKgobLeads.length}</div>
                    </CardContent></Card>
                  </div>
                  {lttKgobLeads.length === 0 ? (
                    <div className="text-center py-12 bg-[#F7F6F2] rounded-lg">
                      <p className="text-[#4C5A70]">No LTT-KGOB leads yet</p>
                      <p className="text-sm text-gray-500 mt-2">Leads from /meetKGOB-LetsTalktaxes will appear here</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead><tr className="border-b-2 border-[#E1DFD7]">
                          <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B2240]">Date</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B2240]">Name</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B2240]">Email</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B2240]">PDF</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B2240]">Status</th>
                        </tr></thead>
                        <tbody>
                          {lttKgobLeads.map((lead) => (
                            <tr key={lead.id} className="border-b border-[#E1DFD7] hover:bg-[#F7F6F2]">
                              <td className="py-4 px-4 text-sm text-[#4C5A70]">{new Date(lead.created_at).toLocaleString()}</td>
                              <td className="py-4 px-4 text-sm font-medium text-[#0B2240]">{lead.first_name}</td>
                              <td className="py-4 px-4 text-sm text-[#4C5A70]">{lead.email}</td>
                              <td className="py-4 px-4">
                                {lead.pdf_downloaded ? (
                                  <Badge className="bg-green-100 text-green-800 text-xs">Downloaded</Badge>
                                ) : (
                                  <Badge className="bg-gray-100 text-gray-600 text-xs">Pending</Badge>
                                )}
                              </td>
                              <td className="py-4 px-4">
                                <Badge className={`text-xs ${
                                  lead.status === 'new' ? 'bg-yellow-100 text-yellow-800' :
                                  lead.status === 'contacted' ? 'bg-blue-100 text-blue-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>{lead.status}</Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 'consultations' && (
          /* Consultations Tab */
          <ConsultationList 
            token={sessionToken} 
            teamMembersList={teamMembersList}
            currentUser={currentUser}
          />
        )}

        {activeTab === 'availability' && currentUser?.role === 'admin' && (
          <AvailabilityTab sessionToken={sessionToken} />
        )}
        
        {activeTab === 'alerts' && currentUser?.role === 'admin' && (
          <AlertsTab sessionToken={sessionToken} />
        )}
        
        {activeTab === 'crmsync' && currentUser?.role === 'admin' && (
          <CRMSyncTab sessionToken={sessionToken} />
        )}

        {activeTab === 'ppcads' && (
          <PpcAdsTab sessionToken={sessionToken} />
        )}

        {activeTab === 'healthcheck' && (
          <HealthCheckTab sessionToken={sessionToken} />
        )}

        {activeTab === 'penaltyrecovery' && (
          <PenaltyRecoveryTab sessionToken={sessionToken} />
        )}

        {activeTab === 'subscriptions' && currentUser?.role === 'admin' && (
          <SubscriptionsTab sessionToken={sessionToken} />
        )}

        {activeTab === 'gya_queue' && currentUser?.role === 'admin' && (
          <GYAQueueTab sessionToken={sessionToken} />
        )}

        {activeTab === 'indexing' && currentUser?.role === 'admin' && (
          <IndexingFixTab sessionToken={sessionToken} />
        )}
        
        {activeTab === 'team' && currentUser?.role === 'admin' && (
          /* Team Management Tab */
          <div className="space-y-6">
            <Card className="bg-ivory">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="text-navy">
                    Team Management {isLoadingTeam && <span className="text-sm text-gray-500 ml-2">(Loading...)</span>}
                  </CardTitle>
                  <Button
                    onClick={() => setShowInviteModal(true)}
                    className="bg-gradient-to-r from-[#E8D89E] via-[#D4AF37] to-[#B99610] text-black font-semibold hover:opacity-90"
                    data-testid="invite-team-member-button"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Invite Team Member
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Error Display */}
                {teamError && (
                  <Alert className="border-red-200 bg-red-50 mb-4">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    <AlertDescription className="text-red-800 font-medium">
                      {teamError}
                    </AlertDescription>
                  </Alert>
                )}

                {teamMembers.length === 0 && !isLoadingTeam ? (
                  <div className="text-center py-12">
                    <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <h3 className="text-lg font-semibold text-navy mb-2">No Team Members Yet</h3>
                    <p className="text-gray-600 mb-4">Start building your team by inviting members</p>
                    <Button data-testid="lead-board-button-3"
                      onClick={() => setShowInviteModal(true)}
                      className="bg-gradient-to-r from-[#E8D89E] via-[#D4AF37] to-[#B99610] text-black font-semibold hover:opacity-90"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Invite Your First Member
                    </Button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-4 text-navy font-semibold">Name</th>
                          <th className="text-left py-3 px-4 text-navy font-semibold">Email</th>
                          <th className="text-left py-3 px-4 text-navy font-semibold">Role</th>
                          <th className="text-left py-3 px-4 text-navy font-semibold">Status</th>
                          <th className="text-left py-3 px-4 text-navy font-semibold">Permissions</th>
                          <th className="text-left py-3 px-4 text-navy font-semibold">Last Login</th>
                          <th className="text-right py-3 px-4 text-navy font-semibold">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {teamMembers.map((member) => (
                          <tr key={member.member_id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-3 px-4 text-navy">
                              {member.name && member.name !== 'Unknown' ? member.name : <span className="text-gray-400 italic">No name set</span>}
                            </td>
                            <td className="py-3 px-4 text-gray-600">{member.email}</td>
                            <td className="py-3 px-4">
                              <Badge variant={member.role === 'admin' ? 'default' : 'secondary'}>
                                {member.role}
                              </Badge>
                            </td>
                            <td className="py-3 px-4">
                              <Badge 
                                variant={member.status === 'active' ? 'default' : member.status === 'pending' ? 'secondary' : 'destructive'}
                                className={member.status === 'active' ? 'bg-green-100 text-green-800' : ''}
                              >
                                {member.status}
                              </Badge>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex flex-wrap gap-1">
                                {member.permissions?.leads?.view && (
                                  <Badge variant="outline" className="text-xs">View Leads</Badge>
                                )}
                                {member.permissions?.leads?.edit && (
                                  <Badge variant="outline" className="text-xs">Edit Leads</Badge>
                                )}
                                {member.permissions?.leads?.delete && (
                                  <Badge variant="outline" className="text-xs">Delete Leads</Badge>
                                )}
                                {member.permissions?.consultations?.view && (
                                  <Badge variant="outline" className="text-xs">View Consultations</Badge>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-4 text-gray-600 text-sm">
                              {member.last_login ? new Date(member.last_login).toLocaleDateString() : 'Never'}
                            </td>
                            <td className="py-3 px-4 text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditMember(member)}
                                className="text-navy hover:text-gold"
                                data-testid={`edit-member-${member.member_id}`}
                              >
                                Edit
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}


        {/* Activity Logs Tab */}
        {activeTab === 'activity' && currentUser?.role === 'admin' && (
          <ActivityLogs token={sessionToken} />
        )}


        {/* Team Invitation Modal */}
        <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-900">
            <DialogHeader>
              <DialogTitle>Invite Team Member</DialogTitle>
              <DialogDescription>
                Send an invitation email with custom permissions. The recipient will receive a registration link.
              </DialogDescription>
            </DialogHeader>
            
            <form data-testid="lead-board-form-2" onSubmit={handleSendInvite} className="space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="invite-email">Email Address *</Label>
                  <Input
                    id="invite-email"
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="colleague@example.com"
                    required
                    data-testid="invite-email-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="invite-name">Full Name *</Label>
                  <Input
                    id="invite-name"
                    type="text"
                    value={inviteName}
                    onChange={(e) => setInviteName(e.target.value)}
                    placeholder="John Doe"
                    required
                    data-testid="invite-name-input"
                  />
                </div>
              </div>

              {/* Permissions Section */}
              <div className="space-y-6 border-t pt-4">
                <h3 className="font-semibold text-navy">Set Permissions</h3>
                
                {/* Leads Permissions */}
                <div className="space-y-3">
                  <h4 className="font-medium text-navy flex items-center gap-2">
                    📊 Leads Management
                  </h4>
                  <div className="grid grid-cols-2 gap-3 pl-6">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="leads-view"
                        checked={invitePermissions.leads.view}
                        onCheckedChange={() => togglePermission('leads', 'view')}
                        data-testid="permission-leads-view"
                      />
                      <label htmlFor="leads-view" className="text-sm cursor-pointer">View Leads</label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="leads-edit"
                        checked={invitePermissions.leads.edit}
                        onCheckedChange={() => togglePermission('leads', 'edit')}
                        data-testid="permission-leads-edit"
                      />
                      <label htmlFor="leads-edit" className="text-sm cursor-pointer">Edit Leads</label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="leads-delete"
                        checked={invitePermissions.leads.delete}
                        onCheckedChange={() => togglePermission('leads', 'delete')}
                        data-testid="permission-leads-delete"
                      />
                      <label htmlFor="leads-delete" className="text-sm cursor-pointer">Delete Leads</label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="leads-export"
                        checked={invitePermissions.leads.export}
                        onCheckedChange={() => togglePermission('leads', 'export')}
                        data-testid="permission-leads-export"
                      />
                      <label htmlFor="leads-export" className="text-sm cursor-pointer">Export Leads</label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="leads-comments"
                        checked={invitePermissions.leads.add_comments}
                        onCheckedChange={() => togglePermission('leads', 'add_comments')}
                        data-testid="permission-leads-comments"
                      />
                      <label htmlFor="leads-comments" className="text-sm cursor-pointer">Add Comments</label>
                    </div>
                  </div>
                </div>

                {/* Consultations Permissions */}
                <div className="space-y-3">
                  <h4 className="font-medium text-navy flex items-center gap-2">
                    📅 Consultations Management
                  </h4>
                  <div className="grid grid-cols-2 gap-3 pl-6">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="consultations-view"
                        checked={invitePermissions.consultations.view}
                        onCheckedChange={() => togglePermission('consultations', 'view')}
                        data-testid="permission-consultations-view"
                      />
                      <label htmlFor="consultations-view" className="text-sm cursor-pointer">View Consultations</label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="consultations-edit"
                        checked={invitePermissions.consultations.edit}
                        onCheckedChange={() => togglePermission('consultations', 'edit')}
                        data-testid="permission-consultations-edit"
                      />
                      <label htmlFor="consultations-edit" className="text-sm cursor-pointer">Edit Consultations</label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="consultations-approve"
                        checked={invitePermissions.consultations.approve_reject}
                        onCheckedChange={() => togglePermission('consultations', 'approve_reject')}
                        data-testid="permission-consultations-approve"
                      />
                      <label htmlFor="consultations-approve" className="text-sm cursor-pointer">Approve/Reject</label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="consultations-delete"
                        checked={invitePermissions.consultations.delete}
                        onCheckedChange={() => togglePermission('consultations', 'delete')}
                        data-testid="permission-consultations-delete"
                      />
                      <label htmlFor="consultations-delete" className="text-sm cursor-pointer">Delete Consultations</label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="consultations-comments"
                        checked={invitePermissions.consultations.add_comments}
                        onCheckedChange={() => togglePermission('consultations', 'add_comments')}
                        data-testid="permission-consultations-comments"
                      />
                      <label htmlFor="consultations-comments" className="text-sm cursor-pointer">Add Comments</label>
                    </div>
                  </div>
                </div>

                {/* Analytics Permissions */}
                <div className="space-y-3">
                  <h4 className="font-medium text-navy flex items-center gap-2">
                    📈 Analytics Access
                  </h4>
                  <div className="grid grid-cols-2 gap-3 pl-6">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="analytics-own"
                        checked={invitePermissions.analytics.view_own}
                        onCheckedChange={() => togglePermission('analytics', 'view_own')}
                        data-testid="permission-analytics-own"
                      />
                      <label htmlFor="analytics-own" className="text-sm cursor-pointer">View Own Analytics</label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="analytics-team"
                        checked={invitePermissions.analytics.view_team}
                        onCheckedChange={() => togglePermission('analytics', 'view_team')}
                        data-testid="permission-analytics-team"
                      />
                      <label htmlFor="analytics-team" className="text-sm cursor-pointer">View Team Analytics</label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Error Display */}
              {inviteError && (
                <Alert className="border-red-200 bg-red-50">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800 font-medium">
                    {inviteError}
                  </AlertDescription>
                </Alert>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button data-testid="lead-board-button-2"
                  type="button"
                  variant="outline"
                  onClick={() => setShowInviteModal(false)}
                  disabled={isSendingInvite}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSendingInvite}
                  className="bg-gradient-to-r from-[#E8D89E] via-[#D4AF37] to-[#B99610] text-black font-semibold hover:opacity-90"
                  data-testid="send-invitation-button"
                >
                  {isSendingInvite ? 'Sending...' : 'Send Invitation'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Team Edit Member Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-900">
            <DialogHeader>
              <DialogTitle>Edit Team Member</DialogTitle>
              <DialogDescription>
                Update team member profile and permissions.
              </DialogDescription>
            </DialogHeader>

            <form data-testid="lead-board-form-1" onSubmit={handleSaveEdit} className="space-y-6">
              {/* Profile Information */}
              <div className="space-y-4">
                <h4 className="font-medium text-navy">Profile Information</h4>
                
                <div>
                  <Label htmlFor="edit-name" className="text-sm font-medium text-navy">
                    Full Name
                  </Label>
                  <Input
                    id="edit-name"
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder="John Doe"
                    required
                    className="mt-1.5"
                    data-testid="edit-member-name"
                  />
                </div>

                <div>
                  <Label htmlFor="edit-email" className="text-sm font-medium text-navy">
                    Email Address
                  </Label>
                  <Input
                    id="edit-email"
                    type="email"
                    value={editEmail}
                    onChange={(e) => setEditEmail(e.target.value)}
                    placeholder="john.doe@example.com"
                    required
                    className="mt-1.5"
                    data-testid="edit-member-email"
                  />
                </div>
              </div>

              {/* Permissions */}
              <div className="space-y-4 pt-4 border-t">
                <h4 className="font-medium text-navy flex items-center gap-2">
                  📊 Lead Permissions
                </h4>
                <div className="grid grid-cols-2 gap-3 pl-6">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="edit-leads-view"
                      checked={editPermissions.leads.view}
                      onCheckedChange={() => toggleEditPermission('leads', 'view')}
                      data-testid="edit-permission-leads-view"
                    />
                    <label htmlFor="edit-leads-view" className="text-sm cursor-pointer">View Leads</label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="edit-leads-edit"
                      checked={editPermissions.leads.edit}
                      onCheckedChange={() => toggleEditPermission('leads', 'edit')}
                      data-testid="edit-permission-leads-edit"
                    />
                    <label htmlFor="edit-leads-edit" className="text-sm cursor-pointer">Edit Leads</label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="edit-leads-delete"
                      checked={editPermissions.leads.delete}
                      onCheckedChange={() => toggleEditPermission('leads', 'delete')}
                      data-testid="edit-permission-leads-delete"
                    />
                    <label htmlFor="edit-leads-delete" className="text-sm cursor-pointer">Delete Leads</label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="edit-leads-export"
                      checked={editPermissions.leads.export}
                      onCheckedChange={() => toggleEditPermission('leads', 'export')}
                      data-testid="edit-permission-leads-export"
                    />
                    <label htmlFor="edit-leads-export" className="text-sm cursor-pointer">Export Leads</label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="edit-leads-comments"
                      checked={editPermissions.leads.add_comments}
                      onCheckedChange={() => toggleEditPermission('leads', 'add_comments')}
                      data-testid="edit-permission-leads-comments"
                    />
                    <label htmlFor="edit-leads-comments" className="text-sm cursor-pointer">Add Comments</label>
                  </div>
                </div>

                <h4 className="font-medium text-navy flex items-center gap-2 pt-2">
                  📅 Consultation Permissions
                </h4>
                <div className="grid grid-cols-2 gap-3 pl-6">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="edit-consultations-view"
                      checked={editPermissions.consultations.view}
                      onCheckedChange={() => toggleEditPermission('consultations', 'view')}
                      data-testid="edit-permission-consultations-view"
                    />
                    <label htmlFor="edit-consultations-view" className="text-sm cursor-pointer">View Consultations</label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="edit-consultations-edit"
                      checked={editPermissions.consultations.edit}
                      onCheckedChange={() => toggleEditPermission('consultations', 'edit')}
                      data-testid="edit-permission-consultations-edit"
                    />
                    <label htmlFor="edit-consultations-edit" className="text-sm cursor-pointer">Edit Consultations</label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="edit-consultations-approve"
                      checked={editPermissions.consultations.approve_reject}
                      onCheckedChange={() => toggleEditPermission('consultations', 'approve_reject')}
                      data-testid="edit-permission-consultations-approve"
                    />
                    <label htmlFor="edit-consultations-approve" className="text-sm cursor-pointer">Approve/Reject</label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="edit-consultations-delete"
                      checked={editPermissions.consultations.delete}
                      onCheckedChange={() => toggleEditPermission('consultations', 'delete')}
                      data-testid="edit-permission-consultations-delete"
                    />
                    <label htmlFor="edit-consultations-delete" className="text-sm cursor-pointer">Delete Consultations</label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="edit-consultations-comments"
                      checked={editPermissions.consultations.add_comments}
                      onCheckedChange={() => toggleEditPermission('consultations', 'add_comments')}
                      data-testid="edit-permission-consultations-comments"
                    />
                    <label htmlFor="edit-consultations-comments" className="text-sm cursor-pointer">Add Comments</label>
                  </div>
                </div>

                <h4 className="font-medium text-navy flex items-center gap-2 pt-2">
                  📈 Analytics Permissions
                </h4>
                <div className="grid grid-cols-2 gap-3 pl-6">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="edit-analytics-own"
                      checked={editPermissions.analytics.view_own}
                      onCheckedChange={() => toggleEditPermission('analytics', 'view_own')}
                      data-testid="edit-permission-analytics-own"
                    />
                    <label htmlFor="edit-analytics-own" className="text-sm cursor-pointer">View Own Analytics</label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="edit-analytics-team"
                      checked={editPermissions.analytics.view_team}
                      onCheckedChange={() => toggleEditPermission('analytics', 'view_team')}
                      data-testid="edit-permission-analytics-team"
                    />
                    <label htmlFor="edit-analytics-team" className="text-sm cursor-pointer">View Team Analytics</label>
                  </div>
                </div>
              </div>

              {/* Error Display */}
              {editError && (
                <Alert className="border-red-200 bg-red-50">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800 font-medium">
                    {editError}
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button data-testid="lead-board-button-1"
                  type="button"
                  variant="outline"
                  onClick={() => setShowEditModal(false)}
                  disabled={isSavingEdit}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSavingEdit}
                  className="bg-gradient-to-r from-[#E8D89E] via-[#D4AF37] to-[#B99610] text-black font-semibold hover:opacity-90"
                  data-testid="save-edit-button"
                >
                  {isSavingEdit ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Export Modal */}
        <ExportModal
          open={showExportModal}
          onClose={() => setShowExportModal(false)}
          onExport={handleExportLeads}
          isLoading={isExporting}
          currentUser={currentUser}
        />

    </LeadboardShell>
  );
}
